import { useQuery } from "@tanstack/react-query";
import { TriangleAlert } from "lucide-react";
import { useMemo } from "react";
import { type DryRunResult, apiDryRun } from "@/lib/reservation-api";
import { tableFeatureLabel } from "@/lib/table-features";
import { useAppStore } from "@/store/store";
import type { Table } from "@/store/types";
import type { ApiTableFeature, ApiWaitlistEntry } from "@/types/api";

/**
 * Validates an entry's currently-picked tables and surfaces the same
 * warnings the reservation edit flow shows:
 *
 * - dry-run failures (pax doesn't fit, missing features, fuera de zona,
 *   mesa ocupada, cocina, ventana cerrada). Reasons come from
 *   ``POST /api/reservations/dry-run/``.
 * - "sobre cupo" — picked mesas are oversized when a strict subset
 *   would already cover ``pax``. Computed client-side because
 *   ``would_succeed`` stays true for oversize picks; same bitmask
 *   subset detector the reservation Sidebar uses.
 */
export function WaitlistDryRunHint({
  entry,
  tableIds,
  startsAt,
  endsAt,
}: {
  entry: ApiWaitlistEntry;
  tableIds: string[];
  startsAt: string;
  endsAt: string;
}) {
  const tables = useAppStore((s) => s.tables);
  const enabled = tableIds.length > 0 && Boolean(startsAt) && Boolean(endsAt);

  const query = useQuery<DryRunResult>({
    queryKey: [
      "waitlist-dry-run",
      entry.id,
      entry.pax,
      startsAt,
      endsAt,
      entry.preferred_zone_id ?? "",
      tableIds.join(","),
      [...entry.features].toSorted().join(","),
    ],
    queryFn: () =>
      apiDryRun({
        pax: entry.pax,
        starts_at: startsAt,
        ends_at: endsAt,
        preferred_zone_id: entry.preferred_zone_id ?? null,
        table_ids: tableIds,
        features: entry.features,
      }),
    enabled,
    staleTime: 5_000,
  });

  // "Sobre cupo": is there a strict subset of the picked mesas whose
  // capacity already covers ``pax``? If so, the operator is using more
  // mesas than needed — same heuristic the Sidebar waste hint uses.
  const wasteInfo = useMemo(() => {
    if (tableIds.length < 2 || entry.pax <= 0) {
      return { isWasteful: false, wastedSeats: 0 };
    }
    const picked = tableIds.map((id) => tables.find((t) => t.id === id)).filter(Boolean) as Table[];
    if (picked.length < 2) return { isWasteful: false, wastedSeats: 0 };
    const fullMax = picked.reduce((acc, t) => acc + (t.maxCapacity ?? 0), 0);
    const fullMask = (1 << picked.length) - 1;
    for (let mask = 1; mask < fullMask; mask++) {
      const subset = picked.filter((_, i) => mask & (1 << i));
      const subMax = subset.reduce((acc, t) => acc + (t.maxCapacity ?? 0), 0);
      if (entry.pax <= subMax) {
        return { isWasteful: true, wastedSeats: Math.max(0, fullMax - entry.pax) };
      }
    }
    return { isWasteful: false, wastedSeats: 0 };
  }, [tableIds, tables, entry.pax]);

  if (!enabled) return null;

  // Failing dry-run wins over the soft "sobre cupo" hint; the operator
  // needs to see the blocker first. ``slot_in_past`` is stripped because
  // waitlist entries are walk-ins (``starts_at = now``) and the "Ese
  // horario ya pasó" copy is meaningless / misleading in that context.
  if (query.data && !query.data.would_succeed) {
    const rawReasons = query.data.reasons?.length
      ? query.data.reasons
      : [query.data.reason ?? "no_table_fit"];
    const reasons = rawReasons.filter((r) => r !== "slot_in_past");
    if (reasons.length > 0) {
      return <DryRunFailureRows data={{ ...query.data, reasons }} />;
    }
  }

  if (wasteInfo.isWasteful) {
    return (
      <p className="mt-1 inline-flex items-start gap-1.5 text-[11px] text-amber-700">
        <TriangleAlert className="mt-0.5 size-3 shrink-0" />
        <span>
          Sobra cupo: {wasteInfo.wastedSeats}{" "}
          {wasteInfo.wastedSeats === 1 ? "puesto extra" : "puestos extra"}. Una mesa más pequeña
          basta.
        </span>
      </p>
    );
  }

  return null;
}

function DryRunFailureRows({ data }: { data: DryRunResult }) {
  const reasons = data.reasons?.length ? data.reasons : [data.reason ?? "no_table_fit"];
  if (reasons.length === 0) return null;

  const ctx = (data.context ?? {}) as Record<string, unknown>;
  const requiredFeatures = Array.isArray(ctx.required) ? (ctx.required as string[]) : null;
  const preferredZoneName =
    typeof ctx.preferred_zone_name === "string" && ctx.preferred_zone_name
      ? (ctx.preferred_zone_name as string)
      : null;
  const outOfZoneNames = Array.isArray(ctx.out_of_zone_table_names)
    ? (ctx.out_of_zone_table_names as string[])
    : null;

  const buildCopy = (code: string): string => {
    switch (code) {
      case "capacity_insufficient":
        return "Esa cantidad de comensales no entra en la mesa elegida";
      case "features_unsupported":
      case "missing_features": {
        if (requiredFeatures && requiredFeatures.length > 0) {
          const labels = requiredFeatures
            .map((f) => tableFeatureLabel(f as ApiTableFeature))
            .join(", ");
          return `La mesa elegida no soporta: ${labels}`;
        }
        return "La mesa elegida no soporta las preferencias del comensal";
      }
      case "no_table_fit":
        return "Ninguna combinación de mesas acomoda esos comensales";
      case "specific_table_busy":
        return "La mesa está ocupada en este momento";
      case "out_of_preferred_zone": {
        if (preferredZoneName && outOfZoneNames && outOfZoneNames.length > 0) {
          return `Mesa fuera de ${preferredZoneName} (${outOfZoneNames.join(", ")})`;
        }
        return "La mesa elegida está fuera de la zona preferida";
      }
      case "zone_inconsistent":
        return "Las mesas elegidas no son consistentes con la zona";
      case "kitchen_cap":
        return "La cocina está saturada en este momento";
      case "outside_hours_or_closure":
        return "El horario está fuera de servicio";
      case "slot_in_past":
        return "Ese horario ya pasó";
      case "invalid_range":
        return "El rango es inválido";
      default:
        return "No funciona con la mesa elegida";
    }
  };

  const [primary, ...rest] = reasons;
  return (
    <div className="mt-1 flex flex-col gap-1 text-[11px] text-amber-700">
      <p className="inline-flex items-start gap-1.5">
        <TriangleAlert className="mt-0.5 size-3 shrink-0" />
        <span>{buildCopy(primary)}</span>
      </p>
      {rest.map((code) => (
        <p key={code} className="ml-4 text-[10px] text-amber-600">
          · también: {buildCopy(code)}
        </p>
      ))}
    </div>
  );
}
